import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBasicGameDetailsQuery } from '@api/games';
import { useBasicCompanyDetailsQuery } from '@api/company';
import { useGetPvxPartnerFilterEntitlementsQuery } from '@api/pvxPartners';
import {
  mapBasicGameDetailsToFilterItems,
  mapBasicCompanyDetailsToFilterItems,
} from '../utils/utils';
import { AccessDetailsItem, FilterListItemMap } from '../components/AppAccessSidepanel/types';

type FilterName = 'game_id' | 'client_code';

type ActiveSidepanel = {
  filterName: FilterName;
  title: string;
  selectedIds: string[];
  filterMap: FilterListItemMap;
  isLoading: boolean;
};

type UsePartnerFilterEntitlementsParams = { clientCode?: string };

type UsePartnerFilterEntitlementsResult = {
  gameIds: string[];
  gameItems: AccessDetailsItem[];
  clientCodes: string[];
  clientCodeItems: AccessDetailsItem[];
  activeSidepanel: ActiveSidepanel | null;
  openSidepanel: (filterName: FilterName, title: string) => void;
  closeSidepanel: () => void;
  handleSidepanelSave: (ids: string[]) => void;
  isLoadingEntitlements: boolean;
};

const toEntitlementItems = (ids: string[], filterMap: FilterListItemMap): AccessDetailsItem[] => {
  const labelByValue = new Map(
    Object.values(filterMap)
      .flat()
      .map((item) => [item.value, item.label ?? item.value]),
  );
  return ids.map((id) => ({
    value: id,
    label: labelByValue.get(id) ?? id,
  }));
};

export const usePartnerFilterEntitlements = ({
  clientCode,
}: UsePartnerFilterEntitlementsParams): UsePartnerFilterEntitlementsResult => {
  const isEditMode = !!clientCode;

  const [gameItems, setGameItems] = useState<AccessDetailsItem[]>([]);
  const [clientCodeItems, setClientCodeItems] = useState<AccessDetailsItem[]>([]);
  const [activeSidepanel, setActiveSidepanel] = useState<{
    filterName: FilterName;
    title: string;
  } | null>(null);

  const {
    data: entitlementsResponse,
    isLoading: isLoadingEntitlementsQuery,
    isFetching: isFetchingEntitlements,
  } = useGetPvxPartnerFilterEntitlementsQuery(clientCode!, { skip: !isEditMode });
  const isLoadingEntitlements =
    isEditMode && (isLoadingEntitlementsQuery || isFetchingEntitlements);

  const { data: gameData, isLoading: isLoadingGames } = useBasicGameDetailsQuery(
    { paginated: false },
    { skip: activeSidepanel?.filterName !== 'game_id' },
  );
  const { data: companyData, isLoading: isLoadingCompanies } = useBasicCompanyDetailsQuery(
    { paginated: false },
    { skip: activeSidepanel?.filterName !== 'client_code' },
  );

  useEffect(() => {
    const result = entitlementsResponse?.data ?? [];
    const games = result.find((item) => item.filterName === 'game_id');
    const clients = result.find((item) => item.filterName === 'client_code');
    if (games)
      setGameItems(games.values.map(({ value, label }) => ({ value, label: label ?? value })));
    if (clients) {
      setClientCodeItems(
        clients.values.map(({ value, label }) => ({ value, label: label ?? value })),
      );
    }
  }, [entitlementsResponse]);

  const gameFilterMap = useMemo(
    () => mapBasicGameDetailsToFilterItems(gameData?.data ?? []),
    [gameData],
  );
  const companyFilterMap = useMemo(
    () => mapBasicCompanyDetailsToFilterItems(companyData?.data ?? []),
    [companyData],
  );

  const openSidepanel = useCallback((filterName: FilterName, title: string) => {
    setActiveSidepanel({ filterName, title });
  }, []);

  const closeSidepanel = useCallback(() => setActiveSidepanel(null), []);

  const handleSidepanelSave = useCallback(
    (ids: string[]) => {
      if (activeSidepanel?.filterName === 'game_id') {
        setGameItems(toEntitlementItems(ids, gameFilterMap));
      }
      if (activeSidepanel?.filterName === 'client_code') {
        setClientCodeItems(toEntitlementItems(ids, companyFilterMap));
      }
    },
    [activeSidepanel, gameFilterMap, companyFilterMap],
  );

  const enrichedActiveSidepanel: ActiveSidepanel | null = activeSidepanel && {
    ...activeSidepanel,
    selectedIds:
      activeSidepanel.filterName === 'game_id'
        ? gameItems.map((item) => item.value)
        : clientCodeItems.map((item) => item.value),
    filterMap: activeSidepanel.filterName === 'game_id' ? gameFilterMap : companyFilterMap,
    isLoading: activeSidepanel.filterName === 'game_id' ? isLoadingGames : isLoadingCompanies,
  };

  return {
    gameIds: gameItems.map((item) => item.value),
    gameItems,
    clientCodes: clientCodeItems.map((item) => item.value),
    clientCodeItems,
    activeSidepanel: enrichedActiveSidepanel,
    openSidepanel,
    closeSidepanel,
    handleSidepanelSave,
    isLoadingEntitlements,
  };
};
